import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postPhoneDispatch = async (confirmationData: {
	confirmationNumber: string;
	poNumber: string;
}) => {
	const url = apiRoutes.POSTPHONEDISPATCH.replace(
		":poNumber",
		confirmationData.poNumber,
	);
	const resp = await postAndValidate(
		url,
		{
			poNumber: confirmationData.poNumber,
			callDate: new Date().toISOString(),
			confirmationNumber: confirmationData.confirmationNumber,
		},
		z.unknown(),
	);
	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostPhoneDispatch = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (confirmationData: {
			confirmationNumber: string;
			poNumber: string;
		}) => postPhoneDispatch(confirmationData),
		onSuccess: () => {
			showSuccess("Shipment dispatched.");
			// Keep the mutation pending until the shipment refetch completes so the
			// modal spinner stays up through the state transition.
			return queryClient.invalidateQueries({ queryKey: ["shipment"] });
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to dispatch. Please try again.",
			);
		},
	});

	return {
		postPhoneDispatch: mutate,
		postPhoneDispatchAsync: mutateAsync,
		isPostingPhoneDispatch: isPending,
		error,
		data,
		resetError: reset,
	};
};
